import {
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ForwardedRef,
  type ElementType,
  type AnchorHTMLAttributes,
  type HTMLAttributes,
  type SyntheticEvent,
} from 'react';
import { type PressEvents } from '../../types';
import { getSafeUrl } from '../../utils/isUnsafeUrl';

export interface UseLinkOptions<T extends HTMLElement = HTMLAnchorElement>
  extends PressEvents<T> {
  /**
   * URL to navigate to.
   */
  href?: string;
  /**
   * Whether the link is disabled.
   */
  isDisabled?: boolean;
  /**
   * The element type to render as. Defaults to 'a'.
   */
  elementType?: ElementType;
  /**
   * Ref to forward to the underlying element.
   */
  ref?: ForwardedRef<T>;
  /**
   * Additional props to pass through to the element.
   */
  props?: HTMLAttributes<T> &
    Partial<
      Pick<
        AnchorHTMLAttributes<HTMLAnchorElement>,
        'target' | 'rel' | 'download'
      >
    > &
    Record<string, unknown>;
}

export interface UseLinkReturn<T extends HTMLElement = HTMLAnchorElement> {
  /**
   * Props to spread on the link element.
   */
  getLinkProps: () => HTMLAttributes<T> &
    Pick<
      AnchorHTMLAttributes<HTMLAnchorElement>,
      'href' | 'target' | 'rel' | 'download'
    > & {
      ref?: ForwardedRef<T>;
    };
  /**
   * Whether the link is currently disabled.
   */
  isDisabled: boolean;
  /**
   * Whether the link is currently being pressed.
   */
  isPressed: boolean;
}

/**
 * Provides link behavior and accessibility for navigation elements.
 * Handles keyboard interactions (Enter only), disabled state, and press events.
 * Supports rendering as different element types (a, span, div, custom components). See useButton for building Button components.
 */
export function useLink<T extends HTMLElement = HTMLAnchorElement>(
  options: UseLinkOptions<T> = {}
): UseLinkReturn<T> {
  const {
    href,
    isDisabled = false,
    onPress,
    onPressStart,
    onPressEnd,
    elementType = 'a',
    ref: forwardedRef,
    props: {
      target,
      rel,
      download,
      onClick,
      onKeyDown,
      onMouseDown,
      onMouseUp,
      tabIndex,
      role,
      ...restProps
    } = {},
  } = options;

  const [isPressed, setIsPressed] = useState(false);

  const isNativeLink = elementType === 'a';

  // Auto-add noopener noreferrer for external links
  const linkRel =
    rel || (target === '_blank' ? 'noopener noreferrer' : undefined);

  const handleClick = (e: MouseEvent<T>) => {
    if (isDisabled) {
      e.preventDefault();
      e.stopPropagation();

      return;
    }

    onPress?.(e);
  };

  const handleKeyDown = (e: KeyboardEvent<T>) => {
    if (isDisabled) {
      e.preventDefault();

      return;
    }

    // Links only activate on Enter (not Space)
    if (e.key === 'Enter') {
      // Native links with href handle Enter naturally (browser converts Enter → click)
      // Links without href or non-semantic elements need explicit activation
      if (!isNativeLink || !href) {
        e.preventDefault();
        // Keyboard activation is instant (no start/end phases like mouse interactions)
        onPress?.(e);
      }
    }
  };

  const handleMouseDown = (e: MouseEvent<T>) => {
    if (isDisabled) return;

    setIsPressed(true);
    onPressStart?.(e);
  };

  const handleMouseUp = (e: MouseEvent<T>) => {
    if (isDisabled) return;

    if (isPressed) {
      setIsPressed(false);
      onPressEnd?.(e);
    }
  };

  const disabledClick = (e: MouseEvent<T>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const disabledKeyDown = (e: KeyboardEvent<T>) => {
    e.preventDefault();
  };

  const getLinkProps = () => {
    const composeHandlers = <E extends SyntheticEvent>(
      userHandler: ((e: E) => void) | undefined,
      internalHandler: (e: E) => void
    ) => {
      return (e: E) => {
        userHandler?.(e);
        internalHandler(e);
      };
    };

    const commonProps = {
      ref: forwardedRef,
      tabIndex: isDisabled ? -1 : tabIndex ?? 0,
      onClick: isDisabled
        ? disabledClick
        : composeHandlers(onClick, handleClick),
      onKeyDown: isDisabled
        ? disabledKeyDown
        : composeHandlers(onKeyDown, handleKeyDown),
      onMouseDown: isDisabled
        ? undefined
        : composeHandlers(onMouseDown, handleMouseDown),
      onMouseUp: isDisabled
        ? undefined
        : composeHandlers(onMouseUp, handleMouseUp),
      ...restProps,
      // Force hook-owned a11y state after `...restProps` so consumer values
      // passed through Link's restProps can't override the disabled contract.
      'aria-disabled': isDisabled || undefined,
      'data-disabled': isDisabled || undefined,
    };

    // Native <a> element
    if (isNativeLink) {
      return {
        ...commonProps,
        // When <a> has no href, its "link" semantics are not consistently exposed to assistive tech.
        // Force role="link" in those cases (and when disabled, since href is stripped). For enabled
        // links with an href, defer to the consumer's `role` so overrides like `role="menuitem"`
        // aren't silently dropped.
        role: !href || isDisabled ? ('link' as const) : role,
        href: isDisabled ? undefined : getSafeUrl(href),
        target: isDisabled ? undefined : target,
        rel: linkRel,
        download,
      };
    }

    // Non-semantic elements (span, div, custom components)
    return {
      ...commonProps,
      role: 'link' as const,
    };
  };

  return {
    getLinkProps,
    isDisabled,
    isPressed,
  };
}
